import React from 'react'
import { useInfiniteQuery } from 'react-query'

import { APP_LOGIN_STORAGE_KEY } from 'configs/auth'
import { api, handleCommonErrors } from 'utils/api'
import {
  NotFoundError,
  NotFoundWithFiltersError,
  InternalServerError,
  NetworkError,
} from 'app/SearchPage/utils/customError'
import useSearchWhitelistResults from 'app/SearchPage/hooks/useSearchWhitelistResults'
import { useContentSearchFilter } from 'stores/content-search'

import type { TResponseSuccessGetContentSearchResult } from 'types/content-search'

export const BASE_PATH = '/guru/teachers/v1alpha1/content_search/search'

function handleError(status, path, error, hasFilters) {
  if (error?.message === 'Network Error') {
    throw new NetworkError(path, error)
  } else if (status === 404) {
    if (hasFilters) {
      throw new NotFoundWithFiltersError(path, error)
    } else {
      throw new NotFoundError(path, error)
    }
  } else if ([400, 403].includes(status) || status >= 500) {
    throw new InternalServerError(path, error)
  } else {
    handleCommonErrors(status, path, error)
  }
}

type TUseAPIContentSearchResultsProps = {
  keyword: string
  params: {
    offset?: number
    limit?: number
    formats?: string[]
    topics?: string[]
  }
}
export function useAPIGetContentSearchResults({
  keyword = '',
  params = { offset: 0, limit: 10, formats: [], topics: [] },
}: TUseAPIContentSearchResultsProps) {
  const cacheKey: Array<any> = [
    'content-search-results-infinite-query',
    keyword,
    { formats: params.formats, topics: params.topics },
  ]
  const {
    hasWhitelistResults,
    whitelistResultsCount,
    whitelistsDataFetched,
    whitelistResults,
  } = useSearchWhitelistResults()
  const contentSearchFilterStore = useContentSearchFilter()
  const hasFilters = React.useMemo(
    () => contentSearchFilterStore.hasAnyFilterSelected(),
    [contentSearchFilterStore]
  )

  return useInfiniteQuery<TResponseSuccessGetContentSearchResult, Error>(
    cacheKey,
    async ({ pageParam }) => {
      try {
        const queryParams = {
          keyword,
          limit: pageParam?.limit || params?.limit,
          offset: pageParam?.offset || params?.offset,
          ...(params.formats.length ? { formats: params.formats } : {}),
          ...(params.topics.length ? { topics: params.topics } : {}),
        }
        const response = await api<TResponseSuccessGetContentSearchResult>(
          BASE_PATH,
          {
            params: queryParams,
          },
          APP_LOGIN_STORAGE_KEY,
          false,
          undefined,
          (param) => param,
          (status, path, error) => {
            handleError(status ?? 500, path, error, hasFilters)
          }
        )

        if (response?.data?.length === 0) {
          if (hasFilters) {
            throw new NotFoundWithFiltersError('/pencarian', new Error())
          } else {
            throw new NotFoundError('/pencarian', new Error())
          }
        }

        return {
          ...response,
          data: [
            ...((pageParam?.offset ?? 0) === 0 ? whitelistResults : []),
            ...(response?.data ?? []),
          ],
          meta: {
            ...(response?.meta ?? {}),
            total: (response?.meta?.total ?? 0) + whitelistResultsCount,
          },
        }
      } catch (error) {
        if (
          error instanceof NotFoundError &&
          hasWhitelistResults &&
          (pageParam?.offset ?? 0) < 10
        ) {
          return {
            data: whitelistResults,
            meta: {
              total: whitelistResultsCount,
              ...params,
            },
          }
        }

        if (pageParam?.offset >= 10) {
          const data = {
            data: [],
            meta: {
              ...(pageParam || params),
              total: pageParam?.total ?? 0,
              offset: pageParam?.offset ?? 0,
              isNextPageError: true,
            },
          }
          return data
        }

        throw error
      }
    },
    {
      getNextPageParam({ meta }) {
        if (meta.total <= (meta?.offset ?? 0) + params.limit) return

        return {
          offset: meta.offset + params.limit,
          limit: params.limit,
          total: meta.total,
        }
      },
      enabled: whitelistsDataFetched,
      useErrorBoundary: true,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}
